import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Button, Tabs, Tab } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import UserSelector from './components/UserSelector.component';
import DashboardFilters from './components/DashboardFilters.component';
import TasksGrid from './components/TasksGrid.component';
import ExpenseFollowUpGrid from './components/ExpenseFollowUpGrid.component';
import RecurringPayablesGrid from './components/RecurringPayablesGrid.component';
import ExternalCommentsGrid from './components/ExternalCommentsGrid.component';
import ExceptionCommentsGrid from './components/ExceptionCommentsGrid.component';
import TaskCallModal from './components/modals/TaskCallModal.component';
import TaskEmailModal from './components/modals/TaskEmailModal.component';
import CreateTaskModal from './components/modals/CreateTaskModal.component';
import MyFilesContainer from './myFiles/myFiles.container';

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: theme.spacing(3),
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  sectionPaper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

const DashboardComponent = ({
  tasks,
  expenseFollowUp,
  recurringPayables,
  externalComments,
  exceptionComments,
  taskComments,
  filters,
  isLoading,
  isLoadingExpenses,
  isLoadingRecurringPayables,
  isLoadingExternalComments,
  isLoadingExceptionComments,
  activeModal,
  selectedTask,
  letterTemplate,
  clients,
  files,
  services,
  regardings,
  vendors,
  letterTemplates,
  users,
  selectedUserId,
  loggedInUserId,
  allEnums,
  onFilterChange,
  onTaskAction,
  onModalClose,
  onCallSubmit,
  onEmailSubmit,
  onCreateTask,
  onCreateTaskModalOpen,
  onUserChange,
  activeFiles,
  coveringFiles,
  closedFiles,
  currentCoverage,
  isCoverageModalOpen,
  setCoverageModalOpen,
  notifications,
  assignCoverage,
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box className={classes.dashboardContainer}>
      <Typography variant="h4" gutterBottom>
        RC Dashboard
      </Typography>

      {/* User selector for testing and IT support */}
      <UserSelector
        users={users}
        selectedUserId={selectedUserId}
        loggedInUserId={loggedInUserId}
        onUserChange={onUserChange}
        isLoading={isLoading}
      />

      {/* Tabs */}
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="My Tasks" />
          <Tab label="My Files" />
        </Tabs>
      </Paper>

      {/* Tab Panel 0: My Tasks */}
      {activeTab === 0 && (
        <Box className={classes.tabPanel}>
          {/* Dashboard-wide filters */}
          <DashboardFilters
            filters={filters}
            onFilterChange={onFilterChange}
            clients={clients}
            files={files}
            isLoading={isLoading}
          />

          {/* Tasks Section */}
          <Paper className={classes.sectionPaper}>
            <Box className={classes.sectionHeader}>
              <Typography variant="h5">
                My Tasks
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={onCreateTaskModalOpen}
                disabled={isLoading}
              >
                Create Task
              </Button>
            </Box>

            {/* Tasks Grid (includes filters) */}
            <TasksGrid
              tasks={tasks}
              onTaskAction={onTaskAction}
              isLoading={isLoading}
              vendors={vendors}
              enumerations={allEnums}
              dashboardFilters={filters}
            />
          </Paper>

          {/* Expense Follow-up Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Expense Follow-up
            </Typography>

            {/* Expense Follow-up Grid (includes filters) */}
            <ExpenseFollowUpGrid
              expenses={expenseFollowUp}
              isLoading={isLoadingExpenses}
              clients={clients}
            />
          </Paper>

          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Active Recurring Payables
            </Typography>

            <RecurringPayablesGrid
              recurringPayables={recurringPayables}
              isLoading={isLoadingRecurringPayables}
            />
          </Paper>

          {/* External Comments Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              External Comments
            </Typography>

            {/* External Comments Grid (includes filters) */}
            <ExternalCommentsGrid
              comments={externalComments}
              isLoading={isLoadingExternalComments}
              clients={clients}
            />
          </Paper>

          {/* Exception Comments Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Exceptions
            </Typography>

            {/* Exception Comments Grid (includes filters) */}
            <ExceptionCommentsGrid
              exceptions={exceptionComments}
              isLoading={isLoadingExceptionComments}
              clients={clients}
            />
          </Paper>
        </Box>
      )}

      {/* Tab Panel 1: My Files */}
      {activeTab === 1 && (
        <Box className={classes.tabPanel}>
          <MyFilesContainer
            selectedUserId={selectedUserId}
          />
        </Box>
      )}

      {/* Modals */}
      <TaskCallModal
        open={activeModal === 'call'}
        task={selectedTask}
        onClose={onModalClose}
        onSubmit={onCallSubmit}
        isLoading={isLoading}
        comments={taskComments}
      />

      <TaskEmailModal
        open={activeModal === 'email'}
        task={selectedTask}
        letterTemplate={letterTemplate}
        onClose={onModalClose}
        onSubmit={onEmailSubmit}
        isLoading={isLoading}
      />

      <CreateTaskModal
        open={activeModal === 'createTask'}
        onClose={onModalClose}
        onSubmit={onCreateTask}
        files={files}
        services={services}
        regardings={regardings}
        vendors={vendors}
        letterTemplates={letterTemplates}
        isLoading={isLoading}
        allEnums={allEnums}
      />
    </Box>
  );
};

DashboardComponent.propTypes = {
  tasks: PropTypes.array,
  expenseFollowUp: PropTypes.array,
  externalComments: PropTypes.array,
  exceptionComments: PropTypes.array,
  taskComments: PropTypes.object,
  filters: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoadingExpenses: PropTypes.bool,
  isLoadingExternalComments: PropTypes.bool,
  isLoadingExceptionComments: PropTypes.bool,
  activeModal: PropTypes.string,
  selectedTask: PropTypes.object,
  letterTemplate: PropTypes.object,
  clients: PropTypes.array,
  files: PropTypes.array,
  services: PropTypes.array,
  regardings: PropTypes.array,
  vendors: PropTypes.array,
  letterTemplates: PropTypes.array,
  users: PropTypes.array,
  selectedUserId: PropTypes.number,
  loggedInUserId: PropTypes.number,
  allEnums: PropTypes.array,
  onFilterChange: PropTypes.func.isRequired,
  onTaskAction: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  onCallSubmit: PropTypes.func.isRequired,
  onEmailSubmit: PropTypes.func.isRequired,
  onCreateTask: PropTypes.func.isRequired,
  onCreateTaskModalOpen: PropTypes.func.isRequired,
  onUserChange: PropTypes.func.isRequired,
};

DashboardComponent.defaultProps = {
  tasks: [],
  expenseFollowUp: [],
  externalComments: [],
  exceptionComments: [],
  taskComments: {},
  filters: {},
  isLoading: false,
  isLoadingExpenses: false,
  isLoadingExternalComments: false,
  isLoadingExceptionComments: false,
  activeModal: null,
  selectedTask: null,
  letterTemplate: null,
  clients: [],
  files: [],
  services: [],
  regardings: [],
  vendors: [],
  letterTemplates: [],
  users: [],
  selectedUserId: null,
  loggedInUserId: null,
  allEnums: [],
};

export default DashboardComponent;
